import __decorate from "../../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html } from "lit";
import { property } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
//#region src/pd-wizard/pd-steps/PdSteps.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var PdSteps = class PdSteps extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.steps = [];
		this.currentStepNr = -99;
		this.styleTyp = "circle";
	}
	static {
		this.styles = [css`
      /* =========================================================================
         HOST & LAYOUT
         ========================================================================= */
      :host {
        --_circle-size: var(--pd-step-circle-size, 40px);
        --_name-font-size: var(--pd-step-name-font-size, 0.9em);
        --_color: var(--pd-step-color, var(--pd-on-primary-col));
        --_bg: var(--pd-step-bg, var(--pd-default-col));

        display: block;
        background-color: var(--_bg);
        padding-top: 0.5em;
        padding-bottom: calc(var(--_name-font-size) * 2.5);
      }

      .parent {
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        position: relative;
      }

      /* =========================================================================
         STEP CONTAINER (state holder)
         ========================================================================= */
      .step-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        z-index: 51;
        width: 100%;
        position: relative;
      }

      /* =========================================================================
         CIRCLE (the clickable step indicator)
         ========================================================================= */
      .step {
        width: var(--_circle-size);
        height: var(--_circle-size);
        border-radius: 50%;
        border: solid 2px var(--_color);
        background-color: var(--_bg);
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        z-index: 51;
      }

      /* =========================================================================
         STEP NUMBER (inside circle)
         ========================================================================= */
      .step-nr {
        font-family: var(--pd-default-font-title-family);
        font-weight: bold;
        font-size: calc(var(--_circle-size) / 1.8);
        color: var(--_color);
        position: absolute;
        pointer-events: none;
      }

      /* =========================================================================
         CONNECTING LINE
         ========================================================================= */
      hr {
        background-color: var(--_color);
        height: 2px;
        position: absolute;
        top: 50%;
        left: 0;
        width: 100%;
        z-index: 50;
        border: none;
        margin: 0;
      }

      /* =========================================================================
         STEP NAME (below circle)
         ========================================================================= */
      .step-name {
        font-family: var(--pd-default-font-title-family);
        font-weight: bold;
        font-size: var(--_name-font-size);
        color: var(--_color);
        position: absolute;
        top: calc(var(--_circle-size) + (var(--_name-font-size) / 2));
      }

      /* =========================================================================
         STATE: CURRENT — filled circle, inverted number
         ========================================================================= */
      .step-container.current .step {
        background-color: var(--pd-step-current-color, var(--_color));
        border-color: var(--pd-step-current-color, var(--_color));
      }

      .step-container.current .step-nr {
        color: var(--_bg);
      }

      .step-container.current .step-name {
        color: var(--pd-step-current-color, var(--_color));
      }

      /* =========================================================================
         STATE: PASSED
         ========================================================================= */
      .step-container.passed .step {
        border-color: var(
          --pd-step-passed-color,
          var(--pd-default-lighter-col)
        );
        background-color: var(--pd-step-passed-bg, var(--_bg));
        cursor: pointer;
      }

      .step-container.passed .step-nr,
      .step-container.passed .step-name {
        color: var(--pd-step-passed-color, var(--pd-default-lighter-col));
      }

      .step-container.passed .step:hover {
        transform: scale(1.1);
        transition: transform 0.15s ease-in-out;
      }

      .step-container.passed .step:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col, var(--pd-default-hover-col));
        outline-offset: 2px;
      }

      /* =========================================================================
         RESPONSIVE
         ========================================================================= */
      @media (max-width: 700px) {
        :host {
          --_circle-size: var(--pd-step-circle-size, 30px);
          --_name-font-size: var(--pd-step-name-font-size, 0.8em);
        }
      }

      /* Mobile (same breakpoint as the stacked wizard buttons): the names
         would overlap each other — show only the current step's name. */
      @media (max-width: 600px) {
        .step-container:not(.current) .step-name {
          display: none;
        }
      }
    `];
	}
	render() {
		return this._renderCircle();
	}
	_renderCircle() {
		return html`
      <div
        class="parent"
        role="list"
        aria-label="${msg("Wizard-Schritte", { id: "pd.steps.aria.label" })}"
      >
        <hr />
        ${this.steps.map((st, index) => {
			const stepNr = index + 1;
			const isCurrent = this.currentStepNr === stepNr;
			const isPassed = this.currentStepNr > stepNr;
			const containerClasses = classMap({
				"step-container": true,
				current: isCurrent,
				passed: isPassed
			});
			const stepLabel = isPassed ? msg(str`Schritt ${stepNr}: ${st.name ?? ""} - abgeschlossen, klicken um zurückzukehren`, { id: "pd.steps.aria.passed" }) : isCurrent ? msg(str`Schritt ${stepNr}: ${st.name ?? ""} - aktueller Schritt`, { id: "pd.steps.aria.current" }) : msg(str`Schritt ${stepNr}: ${st.name ?? ""} - ausstehend`, { id: "pd.steps.aria.pending" });
			return html`
            <div class="${containerClasses}" role="listitem">
              <div
                class="step"
                data-step="${stepNr}"
                tabindex="${isPassed ? "0" : "-1"}"
                role="${ifDefined(isPassed ? "button" : void 0)}"
                aria-label="${stepLabel}"
                aria-current="${ifDefined(isCurrent ? "step" : void 0)}"
                @click="${this._stepClicked}"
                @keydown="${this._handleStepKeydown}"
              >
                <span class="step-nr" aria-hidden="true">${stepNr}</span>
              </div>
              <span class="step-name" aria-hidden="true">${st.name}</span>
            </div>
          `;
		})}
      </div>
    `;
	}
	_stepClicked(e) {
		const stepStr = e.currentTarget.dataset.step;
		this._navigateToStep(stepStr);
	}
	/**
	* Handles keyboard events on step circles.
	* Activates on Enter or Space key.
	*/
	_handleStepKeydown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			const stepStr = e.currentTarget.dataset.step;
			this._navigateToStep(stepStr);
		}
	}
	/**
	* Navigates to a specific step if it's a passed step.
	*/
	_navigateToStep(stepStr) {
		const step = Number(stepStr);
		if (!isNaN(step) && step < this.currentStepNr) this.dispatchEvent(new CustomEvent("go-to", {
			detail: { step },
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ type: Array })], PdSteps.prototype, "steps", void 0);
__decorate([property({ type: Number })], PdSteps.prototype, "currentStepNr", void 0);
__decorate([property({ type: String })], PdSteps.prototype, "styleTyp", void 0);
PdSteps = __decorate([localized()], PdSteps);
//#endregion
export { PdSteps };
